// Shared helpers for custom-field responses.
// `fields` is the array returned by /custom-fields/internal.
// `responses` is an object keyed by custom_field_id, values are strings.

// Build the POST payload array from field definitions + user responses.
// Checkboxes always record their state ('1' if checked, '0' otherwise),
// so the backend can distinguish "unchecked" from "never shown".
// Input and dropdown fields are omitted when empty.
export function buildCustomFieldResponsesPayload(fields, responses) {
  const r = responses || {}
  const out = []

  for (const f of fields || []) {
    const typeId = Number(f.type_id)
    const cfId = f.custom_field_id
    const raw = r[cfId]

    if (typeId === 1) {
      // Always record checkbox state (false is a legitimate answer).
      out.push({
        custom_field_id: Number(cfId),
        response: raw === '1' ? '1' : '0'
      })
    } else if (typeId === 2) {
      if (raw !== undefined && raw !== null && String(raw) !== '') {
        out.push({
          custom_field_id: Number(cfId),
          response: String(raw)
        })
      }
    } else if (typeId === 3) {
      // Dropdown responses are held in-memory as the option object so el-select
      // can match by id. Serialize to the backend's expected string format: just
      // the option name, or JSON {option, follow_up} when a follow-up is present.
      if (raw && typeof raw === 'object' && raw.name) {
        const response = raw.follow_up
          ? JSON.stringify({ option: raw.name, follow_up: raw.follow_up })
          : String(raw.name)
        out.push({ custom_field_id: Number(cfId), response })
      } else if (raw && typeof raw === 'string' && raw.trim() !== '') {
        // Legacy string fallback (pre-refactor data that hasn't been normalized yet).
        out.push({ custom_field_id: Number(cfId), response: String(raw) })
      }
    }
  }

  return out
}

// Returns the subset of fields that should appear given the games currently in scope.
// Fields with visibility != 1 always show. Fields with visibility == 1 only show
// when at least one of the supplied gameIds matches a configured condition.
// gameIds may be a single id, an array, null, or undefined.
export function filterCustomFieldsByGames(fields, gameIds) {
  const ids = (Array.isArray(gameIds) ? gameIds : [gameIds])
    .filter(id => id != null)
    .map(id => Number(id))
  return (fields || []).filter(f => {
    if (Number(f.visibility) !== 1) return true
    const conditions = f.visibility_conditions || []
    return conditions.some(c => ids.includes(Number(c.id)))
  })
}

// Returns true if all required fields have an acceptable answer.
export function validateCustomFieldResponses(fields, responses) {
  const r = responses || {}
  for (const f of fields || []) {
    if (!f.required) continue
    const raw = r[f.custom_field_id]
    const typeId = Number(f.type_id)

    if (typeId === 1) {
      // A required checkbox must be checked ('1'); '' / '0' fail.
      if (raw !== '1') return false
    } else if (typeId === 2) {
      if (raw === undefined || raw === null || !String(raw).trim()) return false
    } else if (typeId === 3) {
      if (!raw) return false
      if (typeof raw === 'object') {
        if (raw.id == null) return false
        if (raw.has_follow_up_field && !String(raw.follow_up || '').trim()) return false
      } else if (typeof raw === 'string' && raw.startsWith('{')) {
        // Legacy string fallback
        try {
          const parsed = JSON.parse(raw)
          if (!parsed || !parsed.option) return false
        } catch (e) {
          if (!String(raw).trim()) return false
        }
      } else if (!String(raw).trim()) {
        return false
      }
    }
  }
  return true
}
